import type { QueryClient } from "@tanstack/react-query";

export const SESSION_QUERY_KEY = ["better-auth", "getSession"] as const;

interface Navigation {
  replace: (to: string) => void;
  currentURL: () => string;
}

/** Each browser document owns its query client and router user context. */
export function createAuthCacheBoundary(
  client: QueryClient,
  navigation: Navigation
) {
  let owner: string | null | undefined;
  let observed: string | null | undefined;
  let explicitChanges = 0;
  let blocked = false;
  const requests = new AbortController();
  const listeners = new Set<() => void>();

  async function clear() {
    // Cancel synchronously before removal. Cancellation discards late results
    // even for query functions/transports that ignore AbortSignal.
    const cancelled = client.cancelQueries({}, { silent: true, revert: false });
    client.clear();
    requests.abort();
    await cancelled;
    client.clear();
  }

  async function complete(to: string) {
    if (blocked) return;
    blocked = true;
    listeners.forEach((listener) => listener());
    await clear();
    // A new document also isolates router matches, subscriptions, and callbacks
    // still holding the retired client (including uncancellable mutations).
    navigation.replace(to);
  }

  async function observeIdentity(identity: string | null) {
    observed = identity;
    if (blocked || explicitChanges) return;
    if (owner === undefined) {
      owner = identity;
    } else if (owner !== identity) {
      await complete(identity === null ? "/login" : navigation.currentURL());
    }
  }

  return {
    complete,
    observeIdentity,
    signal: requests.signal,
    isBlocked: () => blocked,
    canRenderIdentity: (identity: string | null) =>
      owner === undefined || owner === identity,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    beginChange() {
      explicitChanges++;
      return () => {
        explicitChanges--;
        if (!explicitChanges && observed !== undefined)
          void observeIdentity(observed);
      };
    },
  };
}

const boundaries = new WeakMap<
  QueryClient,
  ReturnType<typeof createAuthCacheBoundary>
>();
export function getAuthCacheBoundary(
  client: QueryClient,
  navigation?: Navigation
) {
  let boundary = boundaries.get(client);
  if (!boundary) {
    boundary = createAuthCacheBoundary(
      client,
      navigation ?? {
        replace: (to) => {
          // callbackURL comes from the login URL; keep full-page redirects local.
          let target = "/";
          try {
            const url = new URL(to, window.location.href);
            if (url.origin === window.location.origin) target = url.href;
          } catch {
            /* Invalid destinations fall back to the application root. */
          }
          window.location.replace(target);
        },
        currentURL: () => window.location.href,
      }
    );
    boundaries.set(client, boundary);
  }
  return boundary;
}

/** Password/PIN login and logout must finish the reset before navigation. */
export async function runAuthenticationChange<T>(
  client: QueryClient,
  opts: {
    perform: () => Promise<T>;
    isSuccessful: (result: T) => boolean;
    redirectTo: string;
  }
) {
  const boundary = getAuthCacheBoundary(client);
  const finish = boundary.beginChange();
  try {
    const result = await opts.perform();
    if (opts.isSuccessful(result)) await boundary.complete(opts.redirectTo);
    return result;
  } finally {
    finish();
  }
}
